import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  TextField,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth, getToken } from '../../components/auth/AuthContext';
import AdminTabs from '../../components/admin/AdminTabs';
import { APIHOST } from '../../common';
import { useApp } from '../../components/AppContext';
import ImportConfirmations from '../../components/dialog/ImportConfirmations';
import RefreshIcon from '@mui/icons-material/Refresh';

interface Hotel {
  documentId: string;
  name?: string;
  longName?: string;
}

interface OrderRoomOccupant {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface ConferenceHotelRoom {
  documentId: string;
  name?: string;
}

interface Order {
  documentId: string;
  confirmation?: string;
  contactFirstName?: string;
  contactLastName?: string;
  contactEmail?: string;
  orderStatus?: string;
}

interface OrderRoom {
  id: number;
  documentId: string;
  order?: Order;
  conference_hotel_room?: ConferenceHotelRoom;
  order_room_occupants?: OrderRoomOccupant[];
  arrivalDate?: string;
  departureDate?: string;
  confirmation?: string;
  hotelConfirmation?: string;
}

export default function AdminBookingsPage() {
  const [searchParams] = useSearchParams();
  const hotelIdFromQuery = (searchParams.get('hotelId') || '').trim();
  const { currentUser } = useAuth();
  const { setSnackbarInput } = useApp();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [hotelConfirmations, setHotelConfirmations] = useState<Record<string, string>>({});
  const [savingConfirmations, setSavingConfirmations] = useState<Record<string, boolean>>({});
  const [savedConfirmations, setSavedConfirmations] = useState<Record<string, boolean>>({});
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [hotelConfirmationFilter, setHotelConfirmationFilter] = useState<'all' | 'missing' | 'confirmed'>('all');

  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [selectedHotelId, setSelectedHotelId] = useState<string>(hotelIdFromQuery);

  const [orderRooms, setOrderRooms] = useState<OrderRoom[]>([]);

  const filteredOrderRooms = orderRooms.filter((room) => {
    if (hotelConfirmationFilter === 'all') {
      return true;
    }

    const confirmationValue = (hotelConfirmations[room.documentId] ?? room.hotelConfirmation ?? '').trim();
    const hasHotelConfirmation = confirmationValue.length > 0;

    if (hotelConfirmationFilter === 'missing') {
      return !hasHotelConfirmation;
    }

    return hasHotelConfirmation;
  });

  const confirmedCount = orderRooms.filter((room) => {
    const confirmationValue = (hotelConfirmations[room.documentId] ?? room.hotelConfirmation ?? '').trim();
    return confirmationValue.length > 0;
  }).length;
  const unconfirmedCount = orderRooms.length - confirmedCount;

  const isAuthenticated =
    currentUser &&
    (currentUser.userType === 'internal' || currentUser.userType === 'admin');

  useEffect(() => {
    if (isAuthenticated) {
      fetchHotels();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (selectedHotelId) {
      fetchOrderRooms(selectedHotelId);
    } else {
      setOrderRooms([]);
    }
  }, [selectedHotelId]);

  useEffect(() => {
    if (hotelIdFromQuery && hotelIdFromQuery !== selectedHotelId) {
      setSelectedHotelId(hotelIdFromQuery);
    }
  }, [hotelIdFromQuery, selectedHotelId]);

  const fetchHotels = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(
        `${APIHOST}/api/hotels?sort=longName:asc&pagination[pageSize]=200&pagination[page]=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setHotels(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching hotels:', err);
    }
  };

  const fetchOrderRooms = async (hotelId: string) => {
    try {
      setLoading(true);
      setError('');

      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(
        `${APIHOST}/api/hotel-admin/order-rooms?hotelId=${hotelId}&populate=order,conference_hotel_room,order_room_occupants`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }

      const data = await response.json();
      const rooms: OrderRoom[] = data.data || [];
      setOrderRooms(rooms);
      // Seed local confirmation state from fetched data
      const confirmations: Record<string, string> = {};
      rooms.forEach((r) => {
        confirmations[r.documentId] = r.hotelConfirmation || '';
      });
      setHotelConfirmations(confirmations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bookings');
      console.error('Bookings error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (selectedHotelId) {
      fetchOrderRooms(selectedHotelId);
    }
  };

  const handleHotelConfirmationBlur = async (orderRoomId: string) => {
    const hotelConfirmation = hotelConfirmations[orderRoomId] ?? '';
    setSavingConfirmations((prev) => ({ ...prev, [orderRoomId]: true }));
    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(`${APIHOST}/api/order-rooms/${orderRoomId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: { hotelConfirmation } }),
      });

      if (!response.ok) throw new Error('Failed to save hotel confirmation');

      setSnackbarInput({ message: 'Hotel confirmation saved', severity: 'success' });
      setSavedConfirmations((prev) => ({ ...prev, [orderRoomId]: true }));
    } catch (err) {
      setSnackbarInput({
        message: err instanceof Error ? err.message : 'Failed to save hotel confirmation',
        severity: 'error',
      });
    } finally {
      setSavingConfirmations((prev) => ({ ...prev, [orderRoomId]: false }));
    }
  };

  const handleExportCsv = () => {
    const headers = [
      'Order',
      'Internal Confirmation',
      'Hotel Confirmation',
      'Check In',
      'Check Out',
      'Room Type',
      'Contact Name',
      'Contact Email',
      'Occupants',
    ];

    const escapeCsv = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const rows = filteredOrderRooms.map((room) => {
      const occupants = room.order_room_occupants && room.order_room_occupants.length > 0
        ? room.order_room_occupants
            .map((o) => [o.firstName, o.lastName].filter(Boolean).join(' '))
            .join(', ')
        : '';

      return [
        room.order?.confirmation || '',
        room.confirmation || '',
        hotelConfirmations[room.documentId] ?? room.hotelConfirmation ?? '',
        room.arrivalDate || '',
        room.departureDate || '',
        room.conference_hotel_room?.name || '',
        [room.order?.contactFirstName, room.order?.contactLastName].filter(Boolean).join(' '),
        room.order?.contactEmail || '',
        occupants,
      ].map((v) => escapeCsv(String(v)));
    });

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `bookings_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="warning">Please log in to access this page.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 4, mt: 8 }}>
      <Container maxWidth="lg">
        <AdminTabs currentPath="/admin/bookings" />

        <Paper sx={{ p: 3, mt: 3 }}>
          {/* Hotel Filter */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Hotel</InputLabel>
              <Select
                value={selectedHotelId}
                label="Hotel"
                onChange={(e) => setSelectedHotelId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select a hotel…</em>
                </MenuItem>
                {hotels.map((hotel) => (
                  <MenuItem key={hotel.documentId} value={hotel.documentId}>
                    {hotel.longName || hotel.name || 'Unknown Hotel'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Hotel Confirmation</InputLabel>
              <Select
                value={hotelConfirmationFilter}
                label="Hotel Confirmation"
                onChange={(e) => setHotelConfirmationFilter(e.target.value as 'all' | 'missing' | 'confirmed')}
              >
                <MenuItem value="all">Show All</MenuItem>
                <MenuItem value="missing">Missing</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Loading */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Results */}
          {!loading && selectedHotelId && (
            <>
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                  display: 'flex',
                  gap: 3,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="body2">
                  <strong>Confirmed:</strong> {confirmedCount}
                </Typography>
                <Typography variant="body2">
                  <strong>Unconfirmed:</strong> {unconfirmedCount}
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                   <Button size="small" onClick={handleRefresh} disabled={!selectedHotelId || loading} sx={{ mr: 1 }}>
                    <RefreshIcon fontSize="small" sx={{ mr: 0.5 }} />
                  </Button>
                  <Button variant="contained" size="small" color="warning" onClick={() => setImportDialogOpen(true)} sx={{ mr: 1 }}>
                    Import Confirmations
                  </Button>
                  <Button variant="contained" size="small" onClick={handleExportCsv} disabled={filteredOrderRooms.length === 0}>
                    Export CSV
                  </Button>
                </Box>
              </Box>

              <Typography variant="h6" gutterBottom>
                {filteredOrderRooms.length} Booking{filteredOrderRooms.length !== 1 ? 's' : ''} Found
              </Typography>

              {filteredOrderRooms.length === 0 ? (
                <Alert severity="info">No bookings found for the selected filters.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Order</TableCell>
                        <TableCell>Internal Confirmation</TableCell>
                         <TableCell>Hotel Confirmation</TableCell>
                        <TableCell>Check In</TableCell>
                        <TableCell>Check Out</TableCell>
                        <TableCell>Room Type</TableCell>
                        <TableCell>Contact</TableCell>
                        <TableCell>Occupants</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredOrderRooms.map((room) => (
                        <TableRow key={room.documentId} hover>
                          <TableCell>
                            <Link href={`/admin/order/${room.order?.documentId}`}>
                              {room.order?.confirmation || '—'}
                            </Link>
                          </TableCell>
                          <TableCell>
                            {room.confirmation || '—'}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TextField
                                size="small"
                                variant="outlined"
                                value={hotelConfirmations[room.documentId] ?? ''}
                                onChange={(e) => {
                                  setHotelConfirmations((prev) => ({
                                    ...prev,
                                    [room.documentId]: e.target.value,
                                  }));
                                  setSavedConfirmations((prev) => ({ ...prev, [room.documentId]: false }));
                                }}
                                onBlur={() => handleHotelConfirmationBlur(room.documentId)}
                                inputProps={{ style: { padding: '4px 8px' } }}
                              />
                              {savingConfirmations[room.documentId] ? (
                                <CircularProgress size={16} />
                              ) : (
                                <CheckCircleIcon
                                  fontSize="small"
                                  sx={{ color: savedConfirmations[room.documentId] ? 'success.main' : 'action.disabled' }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {room.arrivalDate ? room.arrivalDate.slice(5) : '—'}
                          </TableCell>
                          <TableCell>
                            {room.departureDate ? room.departureDate.slice(5) : '—'}
                          </TableCell>
                          <TableCell>
                            {room.conference_hotel_room?.name || '—'}
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">
                                {[room.order?.contactFirstName, room.order?.contactLastName]
                                  .filter(Boolean)
                                  .join(' ') || '—'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {room.order?.contactEmail || ''}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {room.order_room_occupants && room.order_room_occupants.length > 0
                              ? room.order_room_occupants
                                  .map((o) =>
                                    [o.firstName, o.lastName].filter(Boolean).join(' ')
                                  )
                                  .join(', ')
                              : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {!loading && !selectedHotelId && (
            <Alert severity="info">Select a hotel to view bookings.</Alert>
          )}
        </Paper>

        <ImportConfirmations
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          onImported={handleRefresh}
        />
      </Container>
    </Box>
  );
}
